'use client';

import { useParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { NewsType } from '@/api/config/types';
import GlobalService from '@/api/services/GlobalService';
import {
  AVI_CAROUSEL_SETTINGS,
  FAQ_CONFIG,
  GAME_DETAIL_CONFIG,
  PRIZE_STORM_CONFIG,
  SLIDER_CONFIG,
  WHY_AVI_CONFIG,
} from '@/app/[lang]/(web)/avi/config';
import Carousel from '@/components/Carousel';
import Image from '@/components/core/Image';
import Link from '@/components/core/Link';
import YouTubePlayer from '@/components/core/YouTubePlayer';
import FAQ from '@/components/FAQ';
import BecomePartner from '@/components/Layout/Footer/components/BecomePartner';
import Features from '@/components/pages/Home/Features';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { useTranslation } from '@/hooks/useTranslations';
import logoPrizeStorm from '@/public/assets/Images/avi/logo-prize-storm.png';
import aviPlane from '@/public/assets/Images/avi/plane1.png';

import './styles.scss';

const PRIZE_STORM_ITEMS = (t: any) =>
  PRIZE_STORM_CONFIG.map(({ title, description1, description2, icon }) => {
    if (title === 'Operator Benefits:') {
      return (
        <div key={title} className="operator-benefits-block">
          <p className="the-ultimate-title">{t(title)}</p>
          {[t(description1), t(description2)].map((description) => (
            <div key={description} className="operator-benefits-row">
              <div className="operator-benefits-flesh-icon">
                <i className={icon} />
              </div>
              <p className="the-ultimate-row-info-info">{description}</p>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div key={title} className="the-ultimate-row-wrp">
        <div
          className={
            icon.includes('impact')
              ? 'the-ultimate-row-impact-block'
              : 'the-ultimate-row-icon-block'
          }
        >
          <i className={icon} />
        </div>
        <div className="the-ultimate-row-info-block">
          <p className="the-ultimate-row-info-title">{t(title)}</p>
          <p className="the-ultimate-row-info-info">{t(description1)}</p>
        </div>
      </div>
    );
  });

const WHY_AVI_ITEMS = (t: any) => {
  return WHY_AVI_CONFIG.map(({ title, image }) => (
    <div key={title} className="why-avi-inner-item">
      <div className="why-avi-inner-item-img-wrp">
        <Image src={image} alt="" />
      </div>
      <p className="why-avi-inner-item-txt">{t(title)}</p>
    </div>
  ));
};

const SLIDER_ITEMS = SLIDER_CONFIG.map((slide, index) => {
  return (
    <div key={index} className="avi-slider-item">
      <Image src={slide} alt="" />
    </div>
  );
});

const GAME_DETAIL_ITEMS = (t: any) => {
  return GAME_DETAIL_CONFIG.map(({ title, description }) => (
    <div key={title} className="avi-game-details-info-row">
      <p className="avi-game-details-info-txt">
        {t(title)} <span>{t(description)}</span>
      </p>
    </div>
  ));
};

const Avi = () => {
  const params = useParams();
  const { t } = useTranslation();
  const { isMobile } = useDeviceSize();

  const [aviNews, setAviNews] = useState<NewsType[] | []>([]);

  useEffect(() => {
    GlobalService.getData<NewsType>('NEWS_URL_FRONT', {
      lang: params.lang,
      brand: 3,
    }).then((data) => {
      if (data?.query) {
        setAviNews(data.query);
      }
    });
  }, []);

  return (
    <div className="avi-wrp">
      <div className="avi-video-wrapper">
        <div className="bigContent">
          <div className="avi-video-holder">
            <div className="avi-video-inner">
              <YouTubePlayer videoId="0xhmUc3fVnM" />
            </div>
          </div>
        </div>
      </div>
      <div className="avi-play-demo-wrp">
        <div className="mediumContent">
          <div className="avi-play-holder">
            <div className="avi-play-logo-wrp">
              <Image src={aviPlane} alt="" />
            </div>
            <p className="avi-pl-title">{t('Avi-Title')}</p>
            <p className="avi-pl-txt-info">{t('Avi-Desc')}</p>
            <div className="avi-pl-btn-wrp">
              <Link
                href={`/${params.lang}/products/games/crash-games/avi-1`}
                className="button btnTypeBig btnTypeLime"
              >
                <p>{t('Avi-Button-playdemo')}</p>
                <i className="bc-i-go-to btnIcon btnIconSmall" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="mediumContent">
        <div className="why-avi-wrapper" id="whyAvi">
          <div className="why-avi-text-block">
            <h1 className="avi-page-title center">{t('Avi-WhyAvi-Title')}</h1>
            <p className="why-avi-text">{t('Avi-WhyAvi-Desc')}</p>
          </div>
          <div className="why-avi-inner-block-wrp">{WHY_AVI_ITEMS(t)}</div>
        </div>

        <div className="avi-prize-storm-wrapper" id="aviPrizeStorm">
          <div className="avi-prize-storm-holder">
            <div className="avi-prize-storm-logo">
              <Image src={logoPrizeStorm} alt="" />
            </div>
            <div className="the-ultimate-wrp">
              <h2 className="the-ultimate-title">{t('Avi-PrizeStorm-Subtitle')}</h2>
              <p className="the-ultimate-txt">{t('Avi-PrizeStorm-Desc')}</p>
              {PRIZE_STORM_ITEMS(t)}
            </div>
          </div>
        </div>
      </div>
      <div className="avi-game-details-wrapper" id="aviGameDetails">
        <div className="avi-game-details-holder">
          <div className="avi-game-details-info-block">
            <h1 className="avi-page-title center">{t('Avi-PrizeStorm-GameDetails')}</h1>
            <div className="avi-game-details-info-inner">{GAME_DETAIL_ITEMS(t)}</div>
          </div>
        </div>
      </div>
      <div className="avi-gallery-wrp" id="aviGallery">
        <h1 className="avi-page-title center">{t('Avi-PrizeStorm-Gallery')}</h1>
        <div className="avi-page-gallery-slider-wrp">
          <Carousel
            slides={SLIDER_ITEMS}
            settings={{
              ...AVI_CAROUSEL_SETTINGS,
              slidesToShow: isMobile ? 1 : 2,
            }}
          />
        </div>
      </div>
      <div className="monopoly-poly-news-wrp" id="aviNews">
        <Features title="Avi-News&Updates" news={aviNews} description="Avi-News&Updates-Desc" />
      </div>
      <div className="mediumContent" id="aviFAQ">
        <FAQ config={FAQ_CONFIG} />
      </div>
      <div className="avi-become-partner-wrp">
        <div className="bigContent">
          <BecomePartner show={true} buttonId="avi-contact-us" />
        </div>
      </div>
    </div>
  );
};

export default Avi;
